import { Component } from '@angular/core';
import { AbstractControl, FormBuilder, FormGroup, Validators } from '@angular/forms';
import { FormBroadcast, FormComponentLookup, FormEntryComponentClass, validatorLookupRegistry } from '@ptl/forms';
import { PtlNotificationService, PtlNotificationStyle } from '@ptl/global-components';
import { SendOOCRequest } from './interfaces/send-outline-of-coverage.interface';
import { SendOutlineOfCoverageService } from './services/send-outline-of-coverage.service';
import { UserInfoStateStructure, LOGIN_INFO_REDUCER_NAME } from '@ptl/account-management';
import { ReducerUtilityService } from 'src/app/shared/utility-services/reducer-utility.service';
import { ProducerStateStructure } from '../../../producer/configs/interfaces/producerStateStructure.interface';
import { PRODUCER_STATE_NAME } from '../../../producer/configs/reducers/producer.reducer';
import { ZipCountyStateStructure } from '../../../shared/interfaces/zipCountyStateStructure.interface';
import { ZIP_COUNTY_STATE_NAME } from '../../../demographics/zip-county-modal/reducers/zipCounty.reducer';
import { PersonalInfoStateStructure } from '../../../shared/interfaces/personalInfoStateStructure.interface';
import { PERSONAL_INFO_STATE_NAME } from '../../../../shared/reducers/personalInfo.reducer';
import { EnrollmentStateStructure } from '../../../shared/interfaces/enrollmentStateStructure.interface';
import { ENROLLMENT_STATE_NAME } from '../../../../shared/reducers/enrollment.reducer';

@FormComponentLookup('form-send-outline-of-coverage')
@Component({
    selector: 'app-send-outline-of-coverage',
    templateUrl: './send-outline-of-coverage.component.html',
    styleUrls: ['./send-outline-of-coverage.component.scss'],
    standalone: false
})
export class SendOutlineOfCoverageComponent extends FormEntryComponentClass {

  isAwaitingStatus = false;
  isConfirmed = false;
  isLoading = false;

  emailForm: FormGroup;
  formControl: AbstractControl;
  statusErrorMsg: string;
  
  baseEvent: FormBroadcast = {
    formControlName: null,
    helperId: null,
    type: 'valueChange',
    value: 'confirm'
  };

  // NOTE:  when on the review page either in the enrollment flow or the producer view app page,
  //        reviewOnly=true for this component (see send-outline-of-coverage-review.component.html)
  //
  // FormEntryComponentClass.reviewOnly: boolean; -- normally differentiates from enrollment and producer view app

  constructor(
    private fb: FormBuilder,
    private reducer: ReducerUtilityService,
    private ptlNotificationService: PtlNotificationService,
    private sendOOCService: SendOutlineOfCoverageService
  ){
    super();
  }

  ngOnInit() {
    // Initialize base event name from entry (not accessible in constructor)
    this.baseEvent.formControlName = this.entry.formControlAttributes.formControlName;

    // Set main entry form control variable for convenience
    this.formControl = this.formControls[this.entry.formControlAttributes.formControlName];

    // Pull in configured state value for ONLY the email form.
    // The overall control should only be populated when the user submits an email
    let defaultEmail = this.getDefaultValue(this.entry.custom.defaultEmailFromState as string);
    this.emailForm = this.fb.group({email: [defaultEmail, [Validators.required, validatorLookupRegistry.get('email')]]});

    // Form control value is unreliable here
    let currentValue = this.reducerUtilityService.getStateFromPath(this.reducerUtilityService.getPayloadStates(), this.getFullStateDotPath());
    if (currentValue?.referralId) {
      this.updateEmailStatus(currentValue.referralId);
    }
  }

  async sendEmail() {
    if (this.emailForm.valid) {
      const email = this.emailForm.controls.email.value;
      this.isLoading = true;

      this.sendOOCService.sendOOCEmail(this.getEmailPayload()).then((response) => {
        if (response?.payload?.messageSent == true) {
          this.notifyEmailSuccess(email, response.payload.referralId);
        }
        else {
          this.sendOOCService.showGenericError();
        }
      })
      .catch((e) => {
        console.error('Error sending OOC email', e);
        this.sendOOCService.showGenericError();
      })
      .finally(() => {
        this.isLoading = false
      });
    }
  }

  notifyEmailSuccess(email: string, referralId: string) {
    // If the app is submitted, we can't alter the last email value, but we can notify the user
    // ** This flow is currently DISABLED from the logic in the HTML template, but it may come back **
    if (this.entry.custom?.isAppSubmitted) {
      this.ptlNotificationService.open({
        title: 'Success',
        content: this.entry.custom.emailNotificationMessage.replace('<<email>>', email),
        primaryButtonText: 'OK',
        notificationStyle: PtlNotificationStyle.SUCCESS
      });
    }
    // If the app is not submitted, we want to update the latest recorded email and referral ID
    else {
      this.formControl.setValue({email: email, referralId: referralId});
    }
  }

  async updateEmailStatus(referralId: string) {
    this.isAwaitingStatus = true;
    this.statusErrorMsg = null;

    this.sendOOCService.getOOCStatus(referralId).then((response) => {
      if (response.lastLinkClickTimestamp) {
        this.isConfirmed = true;
        this.formStreamTrigger.triggerFormStream(this.baseEvent);
      }
    })
    .catch((e) => {
      console.error('Error checking OOC status', e);
      this.statusErrorMsg = 'An error occurred checking the status. Please try again later.'
    })
    .finally(() => {
      this.isAwaitingStatus = false
    });
  }

  getEmailPayload(): SendOOCRequest {
    const EMAIL_OOC_REFERRAL_TYPE = 'OUTLINEOFCOVERAGE';
    const zipcountyState: ZipCountyStateStructure = this.reducer.getState(ZIP_COUNTY_STATE_NAME);
    const producerInfo: ProducerStateStructure = this.reducer.getState(PRODUCER_STATE_NAME);
    const loginInfo: UserInfoStateStructure = this.reducer.getReducerFromApplicationState(LOGIN_INFO_REDUCER_NAME);
    const personalInfo: PersonalInfoStateStructure = this.reducer.getPayloadState(PERSONAL_INFO_STATE_NAME);

    return {
      baseMessage: {},
      payload: {
          member: false,
          deliveryRecipient: this.emailForm.controls.email.value,
          deliveryMethod: 'Email',
          templateName: 'ProducerSharedMedicareBlank',
          memberProfileId: '',
          groupNumber: '',
          umi: '',
          firstName: '',
          lastName: '',
          dob: '',
          etAttributes: [
              {
                  name: 'agentCode',
                  value: producerInfo.selectedAgency.npnNo
              },
              {
                  name: 'agencyNumber',
                  value: producerInfo.selectedAgency.agencyId
              },
              {
                  name: 'Company',
                  value: producerInfo.selectedAgency.agencyName
              },
              {
                  name: 'Brand_Name',
                  value: zipcountyState.county.brand
              },
              {
                  name: 'County',
                  value: zipcountyState.county.county
              },
              {
                  name: 'user_ID',
                  value: loginInfo.userId
              },
              {
                  name: 'EmailTypePreference',
                  value: EMAIL_OOC_REFERRAL_TYPE
              },
              {
                  name: 'Agent_Name',
                  value: loginInfo.userInfo.firstName + ' ' + loginInfo.userInfo.lastName
              },
              {
                  name: 'Agent_Phone',
                  value: loginInfo.userInfo.telephone
              },
              {
                  name: 'clientName',
                  value: personalInfo.firstName + ' ' + personalInfo.lastName
              },
              {
                  name: 'year',
                  value: this.getCoverageYear()
              }
          ]
      }
    }
  }

  // Only works for Medigap
  getCoverageYear(): string {
    let enrollmentState:EnrollmentStateStructure = this.reducer.getPayloadState(ENROLLMENT_STATE_NAME);
    let coverageDate = new Date(enrollmentState.coverageDate);

    return coverageDate?.getFullYear()?.toString();
  }
}
